// CV Nurture taxonomy — role families, the controlled skill vocabulary, and the software list.
//
// Shared because three places need the SAME list and must not drift: the recruiter's candidate-profile
// editor (web), the BDM's "Confirm requirements" card (web), and the matcher (api). A skill a BDM can
// pick that a recruiter cannot tag is a requirement no candidate can ever match.
//
// ⛔ Controlled vocabulary, not free text. "Rostering", "rosters", "roster mgmt" and "shift scheduling"
// are the same skill to a human and four different strings to a matcher. Free-text skills were the
// reason the brief's §5 could not be built on today's data (see docs/CV_NURTURE_DESIGN.md §1).
/**
 * Role families. ⛔ Must equal prisma `enum RoleFamily` exactly — a test reads the schema and compares,
 * because a family added in one place and not the other is a match that can never happen.
 */
export const ROLE_FAMILIES = [
    'ADMIN_EA',
    'CUSTOMER_SERVICE',
    'SCHEDULING_ROSTERING',
    'CLAIMS_BILLING',
    'BOOKKEEPING_ACCOUNTS',
    'RECRUITMENT_SUPPORT',
    'MARKETING',
    'SALES_APPOINTMENTS',
    'PROPERTY_MANAGEMENT',
    'MORTGAGE_BROKING',
    'TECHNICAL_DRAFTING',
    'ALLIED_HEALTH_ADMIN',
    'HR_PAYROLL',
    'DATA_ENTRY',
];
/** What a client reads. Plain role names, the way a business owner would say them. */
export const ROLE_FAMILY_LABEL = {
    ADMIN_EA: 'Admin / Executive Assistant',
    CUSTOMER_SERVICE: 'Customer Service & Reception',
    SCHEDULING_ROSTERING: 'Scheduling & Rostering',
    CLAIMS_BILLING: 'Claims & Billing',
    BOOKKEEPING_ACCOUNTS: 'Bookkeeping & Accounts',
    RECRUITMENT_SUPPORT: 'Recruitment Support',
    MARKETING: 'Marketing Assistant',
    SALES_APPOINTMENTS: 'Sales & Appointment Setting',
    PROPERTY_MANAGEMENT: 'Property Management Assistant',
    MORTGAGE_BROKING: 'Mortgage Broking Assistant',
    TECHNICAL_DRAFTING: 'Drafting & Estimating',
    ALLIED_HEALTH_ADMIN: 'Allied Health Admin',
    HR_PAYROLL: 'HR & Payroll',
    DATA_ENTRY: 'Data Entry',
};
export function isRoleFamily(v) {
    return typeof v === 'string' && ROLE_FAMILIES.includes(v);
}
/**
 * The skill vocabulary. Deliberately small: every entry must be something a recruiter can honestly
 * attest a candidate has, and a BDM would plausibly hear a client ask for. Add with care — a slug, once
 * stored on a profile, is effectively permanent.
 */
export const SKILLS = [
    // Admin / EA
    { slug: 'inbox-management', label: 'Inbox management', family: 'ADMIN_EA', aliases: ['email management', 'managing inbox'] },
    { slug: 'calendar-management', label: 'Diary & calendar management', family: 'ADMIN_EA', aliases: ['diary management', 'scheduling meetings'] },
    { slug: 'document-preparation', label: 'Document preparation', family: 'ADMIN_EA', aliases: ['formatting documents', 'word processing'] },
    { slug: 'minute-taking', label: 'Minute taking', family: 'ADMIN_EA', aliases: ['meeting minutes', 'meeting notes'] },
    { slug: 'spreadsheets-reporting', label: 'Spreadsheets & reporting', family: 'ADMIN_EA', aliases: ['excel', 'google sheets', 'reports'] },
    { slug: 'crm-data-management', label: 'CRM data management', family: 'ADMIN_EA', aliases: ['crm updates', 'crm hygiene'] },
    // Customer service
    { slug: 'phone-reception', label: 'Phone reception & call handling', family: 'CUSTOMER_SERVICE', aliases: ['answering phones', 'reception', 'inbound calls'] },
    { slug: 'client-intake', label: 'Client intake & onboarding', family: 'CUSTOMER_SERVICE', aliases: ['intake', 'new client onboarding'] },
    { slug: 'complaints-handling', label: 'Complaints handling', family: 'CUSTOMER_SERVICE', aliases: ['complaints', 'escalations'] },
    { slug: 'email-chat-support', label: 'Email & live-chat support', family: 'CUSTOMER_SERVICE', aliases: ['live chat', 'email support', 'helpdesk'] },
    // Scheduling & rostering
    { slug: 'rostering', label: 'Rostering & shift management', family: 'SCHEDULING_ROSTERING', aliases: ['roster', 'rosters', 'shift scheduling'] },
    { slug: 'shift-fill', label: 'Shift fill & last-minute cover', family: 'SCHEDULING_ROSTERING', aliases: ['filling shifts', 'cover shifts'] },
    { slug: 'appointment-scheduling', label: 'Appointment scheduling', family: 'SCHEDULING_ROSTERING', aliases: ['booking appointments', 'scheduling appointments'] },
    { slug: 'support-coordination-admin', label: 'Support coordination admin', family: 'SCHEDULING_ROSTERING', aliases: ['support coordination'] },
    // Claims & billing
    { slug: 'ndis-claiming', label: 'NDIS claiming', family: 'CLAIMS_BILLING', aliases: ['ndis claims', 'bulk upload', 'proda'] },
    { slug: 'plan-management', label: 'NDIS plan management', family: 'CLAIMS_BILLING', aliases: ['plan manager', 'plan-managed invoices'] },
    { slug: 'invoicing', label: 'Invoicing', family: 'CLAIMS_BILLING', aliases: ['raising invoices', 'billing'] },
    { slug: 'accounts-receivable', label: 'Accounts receivable & debt follow-up', family: 'CLAIMS_BILLING', aliases: ['debt collection', 'overdue invoices', 'ar'] },
    { slug: 'medicare-dva-billing', label: 'Medicare / DVA billing', family: 'CLAIMS_BILLING', aliases: ['medicare', 'dva', 'bulk billing'] },
    // Bookkeeping & accounts
    { slug: 'bookkeeping', label: 'Bookkeeping', family: 'BOOKKEEPING_ACCOUNTS', aliases: ['books', 'bookkeeper'] },
    { slug: 'bank-reconciliation', label: 'Bank reconciliation', family: 'BOOKKEEPING_ACCOUNTS', aliases: ['reconciliations', 'reconciling'] },
    { slug: 'accounts-payable', label: 'Accounts payable', family: 'BOOKKEEPING_ACCOUNTS', aliases: ['ap', 'paying bills', 'supplier payments'] },
    { slug: 'payroll-processing', label: 'Payroll processing', family: 'HR_PAYROLL', aliases: ['payroll', 'pay runs'] },
    { slug: 'bas-support', label: 'BAS preparation support', family: 'BOOKKEEPING_ACCOUNTS', aliases: ['bas', 'gst'] },
    // Recruitment support
    { slug: 'candidate-sourcing', label: 'Candidate sourcing', family: 'RECRUITMENT_SUPPORT', aliases: ['sourcing', 'headhunting'] },
    { slug: 'candidate-screening', label: 'Candidate screening', family: 'RECRUITMENT_SUPPORT', aliases: ['screening calls', 'phone screens'] },
    { slug: 'job-ad-posting', label: 'Job ad posting', family: 'RECRUITMENT_SUPPORT', aliases: ['posting jobs', 'job ads'] },
    { slug: 'reference-checks', label: 'Reference checks', family: 'RECRUITMENT_SUPPORT', aliases: ['references'] },
    // Marketing
    { slug: 'social-media-management', label: 'Social media management', family: 'MARKETING', aliases: ['social media', 'instagram', 'facebook posts'] },
    { slug: 'content-writing', label: 'Content writing', family: 'MARKETING', aliases: ['blog posts', 'copywriting'] },
    { slug: 'email-marketing', label: 'Email marketing', family: 'MARKETING', aliases: ['newsletters', 'edms'] },
    { slug: 'graphic-design-basic', label: 'Graphic design (Canva)', family: 'MARKETING', aliases: ['canva', 'graphics'] },
    // Sales & appointments
    { slug: 'appointment-setting', label: 'Appointment setting', family: 'SALES_APPOINTMENTS', aliases: ['setting appointments', 'booking meetings'] },
    { slug: 'cold-calling', label: 'Cold calling', family: 'SALES_APPOINTMENTS', aliases: ['outbound calls', 'outbound calling'] },
    { slug: 'lead-qualification', label: 'Lead qualification', family: 'SALES_APPOINTMENTS', aliases: ['qualifying leads'] },
    { slug: 'sales-follow-up', label: 'Sales follow-up', family: 'SALES_APPOINTMENTS', aliases: ['follow ups', 'nurturing leads'] },
    // Property management
    { slug: 'maintenance-coordination', label: 'Maintenance coordination', family: 'PROPERTY_MANAGEMENT', aliases: ['maintenance requests', 'work orders', 'trades coordination'] },
    { slug: 'tenancy-admin', label: 'Tenancy applications & leases', family: 'PROPERTY_MANAGEMENT', aliases: ['leasing', 'tenant applications', 'lease renewals'] },
    { slug: 'rent-arrears', label: 'Rent arrears follow-up', family: 'PROPERTY_MANAGEMENT', aliases: ['arrears'] },
    { slug: 'inspection-scheduling', label: 'Inspection scheduling', family: 'PROPERTY_MANAGEMENT', aliases: ['routine inspections', 'entry notices'] },
    // Mortgage broking
    { slug: 'loan-processing', label: 'Loan processing', family: 'MORTGAGE_BROKING', aliases: ['processing loans', 'loan admin'] },
    { slug: 'application-packaging', label: 'Loan application packaging', family: 'MORTGAGE_BROKING', aliases: ['packaging', 'lodging applications'] },
    { slug: 'document-collection', label: 'Client document collection', family: 'MORTGAGE_BROKING', aliases: ['chasing documents', 'supporting documents'] },
    // Drafting
    { slug: 'cad-drafting', label: 'CAD drafting', family: 'TECHNICAL_DRAFTING', aliases: ['drafting', 'autocad drawings'] },
    { slug: 'estimating', label: 'Estimating & quantity take-off', family: 'TECHNICAL_DRAFTING', aliases: ['take-offs', 'quoting', 'quantity surveying'] },
    // Allied health admin
    { slug: 'clinic-reception', label: 'Clinic reception', family: 'ALLIED_HEALTH_ADMIN', aliases: ['practice reception', 'front desk'] },
    { slug: 'referral-management', label: 'Referral management', family: 'ALLIED_HEALTH_ADMIN', aliases: ['referrals', 'gp referrals'] },
    { slug: 'patient-recalls', label: 'Patient recalls & reminders', family: 'ALLIED_HEALTH_ADMIN', aliases: ['recalls', 'reminders'] },
    // HR
    { slug: 'hr-admin', label: 'HR administration', family: 'HR_PAYROLL', aliases: ['hr', 'human resources'] },
    { slug: 'staff-compliance', label: 'Staff compliance & screening checks', family: 'HR_PAYROLL', aliases: ['worker screening', 'compliance tracking', 'police checks'] },
    // Data entry
    { slug: 'data-entry', label: 'Data entry', family: 'DATA_ENTRY', aliases: ['data input', 'typing'] },
];
const SKILL_BY_SLUG = new Map(SKILLS.map((s) => [s.slug, s]));
export function isSkillSlug(v) {
    return typeof v === 'string' && SKILL_BY_SLUG.has(v);
}
export function skillLabel(slug) {
    return SKILL_BY_SLUG.get(slug)?.label ?? slug;
}
/** Drop unknown slugs and duplicates, keep order. What every write path runs input through. */
export function cleanSkillSlugs(input) {
    const out = [];
    for (const v of input)
        if (isSkillSlug(v) && !out.includes(v))
            out.push(v);
    return out;
}
/**
 * The software a candidate can be tagged with and a client can ask for. MOVED here unchanged from
 * apps/api/src/modules/recruiting/intake.service.ts (which re-exports it), so the apply form, the
 * profile editor and the matcher all read one list.
 */
export const SOFTWARE_GROUPS = [
    { label: 'NDIS', options: ['ShiftCare', 'Brevity', 'Astalty', 'Splose', 'MYP (Managing Your Practice)', 'VisualCare'] },
    { label: 'Tradies / Field Service', options: ['ServiceM8', 'Simpro', 'AroFlo', 'Ascora', 'Fergus'] },
    { label: 'Allied Health', options: ['Cliniko', 'Halaxy', 'Best Practice', 'Zanda', 'Insight'] },
    { label: 'Recruitment', options: ['Bullhorn', 'Vincere', 'JobAdder', 'SEEK', 'LinkedIn Recruiter', 'Indeed Employer'] },
    { label: 'Mortgage Brokers', options: ['ApplyOnline', 'Mercury Nexus', 'AOL', 'MyCRM', 'Podium', 'DocuSign'] },
    { label: 'Accounting & Bookkeeping', options: ['Xero', 'MYOB', 'QuickBooks Online', 'Dext', 'Hubdoc'] },
    { label: 'Property Management', options: ['PropertyMe', 'Console Cloud', 'PropertyTree', 'Inspection Express', 'IRE'] },
    { label: 'Drafting / Construction', options: ['AutoCAD', 'Revit', 'Tekla', 'SketchUp', 'Bluebeam Revu', 'Autodesk Construction Cloud'] },
];
export const SOFTWARE = [...new Set(SOFTWARE_GROUPS.flatMap((g) => g.options))];
/** Case- and space-insensitive key for comparing software names ("propertyme" === "PropertyMe "). */
export function softwareKey(name) {
    return name.trim().toLowerCase().replace(/\s+/g, ' ');
}
/** Map arbitrary input onto canonical software names; unknown names are dropped, not invented. */
export function cleanSoftware(input) {
    const byKey = new Map(SOFTWARE.map((s) => [softwareKey(s), s]));
    const out = [];
    for (const v of input) {
        if (typeof v !== 'string')
            continue;
        const canon = byKey.get(softwareKey(v));
        if (canon && !out.includes(canon))
            out.push(canon);
    }
    return out;
}
//# sourceMappingURL=nurture.js.map